import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';
import { track } from '../../utils/analytics';
const EASE = [0.23, 1, 0.32, 1] as const;
export function GuaranteePriceSection() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const onCta = (e: React.MouseEvent) => {
    e.preventDefault();
    track('price_cta_click');
    document.getElementById('discuss')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="bg-white py-20 lg:py-28" ref={ref} data-id="element-408" data-mp="30elp9xptxg47">
      <div className="max-w-[88rem] mx-auto px-5 sm:px-8" data-id="element-409" data-mp="ap1hlblsiunt">
        <div className="grid lg:grid-cols-2 border-t border-l border-line" data-id="element-410" data-mp="aqmcx3fxihmq">
          {/* Guarantee */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.55,
          ease: EASE
        }} className="border-r border-b border-line p-8 lg:p-14 flex flex-col" data-id="element-411" data-mp="fppfmh8fsbrj">
            <ShieldCheckIcon className="w-6 h-6 text-accent" aria-hidden="true" data-id="element-412" data-mp="f34oxs2ajnzq" />
            <h2 className="mt-6 font-display text-[1.75rem] lg:text-[2.25rem] font-bold text-ink tracking-[-0.03em] leading-[1.1] max-w-[20ch]" data-id="element-413" data-mp="a7pc93g0ry4w">
              {c.guarantee.title}
            </h2>

            <div className="mt-10 pt-8 border-t border-line" data-id="element-414" data-mp="dp04hmblsmo">
              <div className="font-display text-[3.5rem] lg:text-[5rem] font-bold text-ink tracking-[-0.05em] leading-none" data-id="element-415" data-mp="3rn4j0dxpxnn5">
                {c.guarantee.value}
              </div>
              <div className="mt-3 text-[0.9375rem] text-muted" data-id="element-416" data-mp="3ily2w5yzeeuf">
                {c.guarantee.valueLabel}
              </div>
            </div>

            <p className="mt-8 text-[1.0625rem] text-muted leading-relaxed max-w-[46ch]" data-id="element-417" data-mp="3dn3i4yhgeg7o">
              {c.guarantee.text}
            </p>
          </motion.div>

          {/* Price */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.55,
          delay: 0.1,
          ease: EASE
        }} className="border-r border-b border-line bg-sand p-8 lg:p-14 flex flex-col" data-id="element-418" data-mp="38zdknwdgp0fb">
            <h2 className="font-display text-[1.75rem] lg:text-[2.25rem] font-bold text-ink tracking-[-0.03em] leading-[1.1] max-w-[20ch]" data-id="element-419" data-mp="3mqicwdei2qfm">
              {c.price.title}
            </h2>

            <p className="mt-8 text-[1.0625rem] text-ink" data-id="element-420" data-mp="3cycmv97t6j32">{c.price.lead}</p>

            <ul className="mt-5 border-t border-line" data-id="element-421" data-mp="3hxyzbgftbvat">
              {c.price.factors.map(f => <li key={f} className="flex items-baseline gap-3 py-3.5 border-b border-line text-[1.0625rem] text-ink" data-id="element-422" data-mp="9i5jl3tb2nut">
                  <span className="w-1.5 h-1.5 flex-shrink-0 bg-accent translate-y-[-0.15rem]" aria-hidden="true" data-id="element-423" data-mp="eb8bz8mcjr9f" />
                  {f}
                </li>)}
            </ul>

            <p className="mt-7 text-[0.9375rem] text-muted leading-relaxed max-w-[42ch]" data-id="element-424" data-mp="28okok995h5ji">
              {c.price.note}
            </p>

            <div className="mt-auto pt-10" data-id="element-425" data-mp="23sm1cozk5tko">
              <a href="#discuss" onClick={onCta} className="group inline-flex w-full sm:w-auto items-center justify-center gap-2.5 px-7 py-4 text-base font-semibold text-white bg-accent hover:bg-accent-600 transition-colors duration-200" data-id="element-426" data-mp="1g059jmq0qn86">
                {c.price.cta}
                <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-427" data-mp="1kygky1vyp5si" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}